import React, { useContext, useEffect, useState } from 'react'
import useSWR from 'swr'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Single_Book } from '@graphql/queries'
import { AppContext } from '@state/context'
import { closePanel, openPanel } from '@state/actions/panelsActions'
import {
  setSelectedBookTitle,
} from '@state/actions/appActions'
import { useUpdateBook } from '@hooks/mutations/useUpdateBook'
import { getSubjectTextClass } from '@helpers/subject'
import {
  PANELS,
  DIVIDER_SIZE,
  BUTTON_SIZE,
  BUTTON_TYPE,
  HEADING_TYPE,
  TEXT_SIZES,
  CONTENT_TYPES,
} from '@helpers/constants'
import Text from '@components/Text'
import Heading from '@components/Heading'
import Divider from '@components/Divider'
import Loader from '@components/Loader'
import Icon from '@components/Icon'
import Button from '@components/Button'
import ButtonWithLoader from '@components/ButtonWithLoader'
import { useConfirm } from '@hooks/useConfirm'
import { useDeleteBook } from '@hooks/mutations/useDeleteBook'
import SubjectAreaList from '@components/SubjectAreaList'
import BlockRenderer from '@views/BlockRenderer'

const ViewBookPanel = () => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [state, dispatch] = useContext(AppContext)
  const {
    app: { selectedBookId: bookId, userId },
  } = state

  const { data, error } = useSWR([Single_Book, bookId])
  const [book, setBook] = useState(null)

  useEffect(() => {
    if (data && data.book && !error) setBook(data.book)
  }, [data, error])


  const isOwnBook = book?.creator?.id === userId
  const isSaved = book?.customers.some((customer) => customer?.id === userId)

  const { post, isPosting } = useUpdateBook()
  const { confirm } = useConfirm()

  const updateBook = async (
    connectCustomers = [],
    disconnectCustomers = []
  ) => {
    if (disconnectCustomers.length > 0) {
      const confirmed = await confirm({
        title: book.name,
        text: 'Är du säker på att du vill ta bort den här boken från din bokhylla?',
        confirmText: 'Ja, ta bort bok',
        cancelText: 'Avbryt',
      })
      if (!confirmed) return
    }
    const res = await post(
      {
        id: bookId,
        connectCustomers: connectCustomers,
        disconnectCustomers: disconnectCustomers,
      },
      connectCustomers.length > 0
        ? 'Sparad i bokhyllan.'
        : 'Borttagen från bokhyllan.',
      connectCustomers.length > 0
        ? 'Nåt gick fel när boken skulle sparas i bokhyllan.'
        : 'Nåt gick fel när boken skulle tas bort från bokhyllan.'
    )
  }

  const { post: postDelete, isPosting: isPostingDelete } = useDeleteBook()
  const deleteBook = async () => {
    const confirmed = await confirm({
      title: book.name,
      text: 'Är du säker på att du vill radera denna bok? Den raderas helt och hållet, och försvinner från dina klasser/grupper.',
      confirmText: 'Ja, radera bok',
      cancelText: 'Avbryt',
    })

    if (confirmed) {
      const res = await postDelete({
        id: book.id,
      })

      if (res.success) {
        dispatch(closePanel(PANELS.VIEW_BOOK))
        if (pathname.split('/')[1] === 'bok') {
          navigate('/bokhylla')
        }
      }
    }
  }

  return (
    <div>
      {!data && !error && <Loader />}
      {error && <Text>Nåt gick fel när vi skulle ladda boken</Text>}
      {book && (
        <>
          <div className={'flex justify-between'}>
            <Heading
              color={`text-subject-${book.subject.slug}-light`}
              headingLevel={HEADING_TYPE.H2}
              size={TEXT_SIZES.BASE}
            >
              {book.subject.name}
            </Heading>
            <Button
              handleClick={() => {
                dispatch(closePanel(PANELS.VIEW_BOOK))
              }}
              size={BUTTON_SIZE.NO_PADDING}
              icon={'Close'}
            ></Button>
          </div>
          <div>
            <Heading headingLevel={HEADING_TYPE.H2} size={TEXT_SIZES.XLARGE}>
              {book.name}
            </Heading>

            <BlockRenderer content={book?.description} />
          </div>
          <Divider size={DIVIDER_SIZE.SMALL} />

          <div className={'flex'}>
            <Text extraClasses={'mr-2'} bold>
              Årskurs:
            </Text>
            <Text>{book.schoolYear.name}</Text>
          </div>

          <div className={'flex'}>
            <Text extraClasses={'mr-2'} bold>
              Nivå:
            </Text>
            <Text>{book.level.name}</Text>
          </div>

          <Divider size={DIVIDER_SIZE.SMALL} />

          <Text bold>Avsnitt</Text>

          {book.sections.length > 0 ? (
            <div className={'flex flex-col items-start'}>
              {book.sections.map((section, index) => {
                const { id, name, subject } = section
                return (
                  <Link
                    key={`section-link-${id}`}
                    className={'flex items-center'}
                    to={`/avsnitt/${id}`}
                  >
                    <Icon
                      icon={'Section'}
                      color={getSubjectTextClass(subject?.slug)}
                    />
                    <Text extraClasses={'ml-2'}>{name}</Text>
                  </Link>
                )
              })}
            </div>
          ) : (
            <Text>Inga avsnitt i boken än</Text>
          )}

          <Divider size={DIVIDER_SIZE.SMALL} />

          <SubjectAreaList
            item={book}
            contentType={CONTENT_TYPES.BOOK}
          />

          <Divider size={DIVIDER_SIZE.SMALL} />

          <Button
            type={BUTTON_TYPE.SUBJECT}
            size={BUTTON_SIZE.LARGE}
            subject={book?.subject?.slug}
            to={`/bok/${book.id}`}
            handleClick={() => {
              dispatch(closePanel(PANELS.VIEW_BOOK))
            }}
          >
            Öppna
          </Button>

          <Divider size={DIVIDER_SIZE.SMALL} />

          <div className={'flex'}>
            <Button
              type={BUTTON_TYPE.DARK_GREY}
              size={BUTTON_SIZE.LARGE}
              icon={'Share'}
              iconRight
              marginRight
              handleClick={() => {
                // dispatch(setSelectedBookId(book.id))
                dispatch(setSelectedBookTitle(book.name))
                dispatch(openPanel(PANELS.SHARE_BOOK))
              }}
            >
              Dela med klass
            </Button>

            {isOwnBook ?
              <ButtonWithLoader
                type={BUTTON_TYPE.ALERT_RED_DARK}
                size={BUTTON_SIZE.LARGE}
                isLoading={isPostingDelete}
                enabledText={'Radera bok'}
                disabled={isPostingDelete}
                handleClick={deleteBook}
                nativeTypeAttribute={'button'}
                icon={'Exit'}
                iconRight
              />
            :
              <ButtonWithLoader
                type={BUTTON_TYPE.DARK_GREY}
                size={BUTTON_SIZE.LARGE}
                isLoading={isPosting}
                icon={isSaved ? 'Exit' : 'Favourite'}
                iconRight
                enabledText={
                  !isSaved ? 'Spara i bokhylla' : 'Ta bort från bokhyllan'
                }
                disabled={isPosting}
                handleClick={() => {
                  !isSaved
                    ? updateBook([userId], []) // connect
                    : updateBook([], [userId]) // disconnect
                }}
              />
            }
          </div>
        </>
      )}
    </div>
  )
}

export default ViewBookPanel
